import { normalizarDocumento, normalizarIdComparacao } from "./documento.js";

/**
 * Roles reconhecidos pelo sistema (espelho das constantes usadas no
 * CadastroEmpresa e em outras páginas internas). Mantidos localmente
 * para evitar acoplamento e permitir testes unitários.
 */
export const NIVEL_EMPRESA = 1;
export const NIVEL_ADMIN_VISUALIZADOR = 2;
export const NIVEL_CONSULTOR = 3;
export const NIVEL_SUPEX = 4;

/**
 * Estudante (nível 1 do tipo_acesso do CIEEMG, ou `acesso_autorizacao === 1`
 * no JWT). Vê apenas as autorizações vinculadas ao próprio CPF — uma por
 * empresa — e cada item é tratado de forma individual.
 */
export const NIVEL_ESTUDANTE = 10;

const CHAVES_ESTUDANTE_NO_JWT = [
  "tipo",
  "acesso_autorizacao",
];

/**
 * Identifica se o usuário logado é um estudante do fluxo TCE/TCA
 * Empregador. Considera tanto o JWT quanto o fallback no localStorage
 * para evitar divergências entre as duas fontes.
 */
export function usuarioEhEstudante(payloadJwt = {}) {
  if (!payloadJwt || typeof payloadJwt !== "object") return false;
  if (payloadJwt.tipo === "estudante") return true;
  if (Number(payloadJwt.acesso_autorizacao ?? 0) === 1) return true;
  return false;
}

const CAMPOS_USUARIO_ID = [
  "usuario_id",
  "id_usuario",
  "usuarioId",
  "idUsuario",
  "user_id",
  "id",
  "ID",
  "created_by",
  "createdBy",
  "criado_por",
  "criadoPor",
  "cadastrado_por",
  "cadastradoPor",
  "id_cadastrador",
  "idCadastrador",
  "usuario_cadastro_id",
  "usuarioCadastroId",
  "consultor_id",
  "consultorId",
  "id_consultor",
  "idConsultor",
];

const CAMPOS_USUARIO_DOCUMENTO = [
  "documento_usuario",
  "documentoUsuario",
  "documento_cadastrador",
  "documentoCadastrador",
  "cadastrado_por_documento",
  "cadastradoPorDocumento",
  "usuario_documento",
  "usuarioDocumento",
  "cpf_usuario",
  "cpfUsuario",
  "cnpj_usuario",
  "cnpjUsuario",
  "documento",
  "cpf",
  "cnpj",
  "CPF",
  "CNPJ",
];

const SUB_OBJETOS = ["usuario", "cadastrador", "responsavel", "empresa", "cadastro"];

function flattenValores(obj, profundidadeMax = 3, profundidade = 0, acumulado = []) {
  if (!obj || typeof obj !== "object" || profundidade > profundidadeMax)
    return acumulado;

  if (Array.isArray(obj)) {
    obj.forEach((item) =>
      flattenValores(item, profundidadeMax, profundidade + 1, acumulado),
    );
    return acumulado;
  }

  for (const [chave, valor] of Object.entries(obj)) {
    if (
      CAMPOS_USUARIO_ID.includes(chave) ||
      CAMPOS_USUARIO_DOCUMENTO.includes(chave)
    ) {
      acumulado.push(valor);
    } else if (SUB_OBJETOS.includes(chave) && valor && typeof valor === "object") {
      flattenValores(valor, profundidadeMax, profundidade + 1, acumulado);
    }
  }

  return acumulado;
}

/**
 * Verifica se a empresa foi cadastrada pelo consultor logado,
 * tolerando variações no payload do backend.
 */
export function empresaFoiCadastradaPeloConsultor(
  item,
  usuarioIdLogado,
  documentoLogado,
) {
  const raw = item?.raw || item || {};

  const usuarioIdNormalizado = normalizarIdComparacao(usuarioIdLogado);
  const documentoNormalizado = normalizarDocumento(documentoLogado);

  if (!usuarioIdNormalizado && !documentoNormalizado) return false;

  const valores = flattenValores(raw);

  if (usuarioIdNormalizado) {
    const matchPorId = valores
      .filter((valor) => valor !== null && String(valor).trim())
      .map(normalizarIdComparacao)
      .some((id) => id && id === usuarioIdNormalizado);

    if (matchPorId) return true;
  }

  if (documentoNormalizado) {
    const matchPorDocumento = valores
      .map(normalizarDocumento)
      .some((doc) => doc && doc === documentoNormalizado);

    if (matchPorDocumento) return true;
  }

  return false;
}
